import { useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent, type ReactNode } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CommuteOutlinedIcon from '@mui/icons-material/CommuteOutlined';
import DirectionsBikeOutlinedIcon from '@mui/icons-material/DirectionsBikeOutlined';
import DirectionsBusOutlinedIcon from '@mui/icons-material/DirectionsBusOutlined';
import DirectionsWalkOutlinedIcon from '@mui/icons-material/DirectionsWalkOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import LocalParkingOutlinedIcon from '@mui/icons-material/LocalParkingOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import TrainOutlinedIcon from '@mui/icons-material/TrainOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  useCreateMyClaim,
  useExpenseTypes,
  useMyCategory,
  useMyClaims,
  useMyCommuteAddresses,
  useMyProfile,
  useReviseMyRejectedClaim,
  useSubmitMyClaim,
  useUploadMyAttachment,
} from '@/hooks/useMobility';
import { getApiErrorMessage } from '@/api/client';
import { mobilitySelfApi } from '@/api/mobility';
import { formatCurrency } from '@/utils/currency';
import { formatBelgianDate } from '@/utils/date';
import { getMobilityYearOptions } from '@/utils/mobilityYears';
import { getClaimStatusColor } from '@/utils/claimStatus';
import { BelgianDateField } from '@/components/BelgianDateField';
import type { CreateMyExpenseClaim, ExpenseClaim, ExpenseType, PostalAddress } from '@/types/mobility';

/**
 * The employee's Pillar 2 expense claims page: a year-filtered list of the
 * caller's claims, plus dialogs to create a new claim, view claim details,
 * attach or download proof documents, submit drafts, and revise/resubmit
 * rejected claims.
 *
 * Shows a message instead of the list when the signed-in account isn't
 * linked to a mobility profile, or when mobility budget isn't enabled.
 *
 * @returns The claims page.
 */
export function MobilityMyClaimsPage() {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [creating, setCreating] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState<ExpenseClaim | null>(null);
  const [revisingClaim, setRevisingClaim] = useState<ExpenseClaim | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachTargetRef = useRef<number | null>(null);

  const { data: profile, isLoading: profileLoading } = useMyProfile();
  const linked = profile?.isLinked ?? false;
  const budgetEnabled = profile?.mobilityBudgetEnabled ?? false;
  const { data: category } = useMyCategory(linked);
  const yearOptions = useMemo(
    () => getMobilityYearOptions(category?.vehicleLeaseStartDate, category?.vehicleLeaseEndDate),
    [category?.vehicleLeaseStartDate, category?.vehicleLeaseEndDate],
  );

  const { data: claims, isLoading } = useMyClaims(year, linked && budgetEnabled);
  const submit = useSubmitMyClaim();
  const upload = useUploadMyAttachment();

  const onSubmit = (claim: ExpenseClaim) => {
    submit.mutate(claim.id, {
      onSuccess: () => enqueueSnackbar(t('mobility.self.submitted'), { variant: 'success' }),
      onError: (error) => enqueueSnackbar(getApiErrorMessage(error), { variant: 'error' }),
    });
  };

  const onAttachClick = (claim: ExpenseClaim) => {
    attachTargetRef.current = claim.id;
    fileInputRef.current?.click();
  };

  const onFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const claimId = attachTargetRef.current;
    event.target.value = '';
    if (!file || claimId === null) return;
    upload.mutate(
      { claimId, file },
      {
        onSuccess: () => enqueueSnackbar(t('mobility.self.attachmentAdded'), { variant: 'success' }),
        onError: (error) => enqueueSnackbar(getApiErrorMessage(error), { variant: 'error' }),
      },
    );
  };

  const columns: GridColDef<ExpenseClaim>[] = useMemo(
    () => [
      { field: 'expenseTypeName', headerName: t('mobility.self.expenseType'), flex: 1, minWidth: 160 },
      {
        field: 'amount',
        headerName: t('mobility.self.amount'),
        width: 130,
        valueFormatter: (_value, row) => formatClaimAmount(row, i18n.language, t('mobility.self.amountToBeCalculated')),
      },
      {
        field: 'expenseDate',
        headerName: t('mobility.self.date'),
        width: 120,
        valueFormatter: (value) => formatBelgianDate(value as string),
      },
      {
        field: 'attachments',
        headerName: t('mobility.fields.attachments'),
        width: 100,
        valueGetter: (_value, row) => row.attachments.length,
      },
      {
        field: 'status',
        headerName: t('mobility.fields.status'),
        width: 140,
        renderCell: (params) => <Chip size="small" color={getClaimStatusColor(params.row.status)} label={t(`mobility.claimStatus.${params.value}`)} />,
      },
      {
        field: 'actions',
        headerName: t('mobility.fields.actions'),
        width: 260,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const claim = params.row;
          const isDraft = claim.status === 'Draft';
          return (
            <Stack direction="row" spacing={0.5}>
              {isDraft && (
                <Button size="small" startIcon={<AttachFileIcon />} onClick={(event) => { event.stopPropagation(); onAttachClick(claim); }}>
                  {t('mobility.self.addAttachment')}
                </Button>
              )}
              {isDraft && (
                <Button size="small" color="primary" startIcon={<SendIcon />} onClick={(event) => { event.stopPropagation(); onSubmit(claim); }}>
                  {t('mobility.self.submit')}
                </Button>
              )}
            </Stack>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language],
  );

  if (!profileLoading && !linked) {
    return (
      <Typography sx={{ mt: 2 }} color="text.secondary">
        {t('mobility.self.notLinked')}
      </Typography>
    );
  }

  if (!profileLoading && linked && !budgetEnabled) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography fontWeight={700}>{t('mobility.self.budgetInactiveTitle')}</Typography>
        {t('mobility.self.budgetInactiveMessage')}
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      <input ref={fileInputRef} type="file" hidden onChange={onFileSelected} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5">{t('mobility.self.claimsTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('mobility.self.claimsSubtitle')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            select
            size="small"
            label={t('mobility.fields.year')}
            value={year}
            onChange={(event) => setYear(Number(event.target.value))}
            sx={{ minWidth: 120 }}
          >
            {yearOptions.map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            {t('mobility.self.newClaim')}
          </Button>
        </Stack>
      </Stack>

      <DataGrid
        autoHeight
        rows={claims ?? []}
        columns={columns}
        loading={isLoading}
        disableRowSelectionOnClick
        onRowClick={({ row }) => setSelectedClaim(row)}
        sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        pageSizeOptions={[10, 25, 50]}
      />

      {creating && <CreateClaimDialog year={year} onClose={() => setCreating(false)} />}
      {selectedClaim && (
        <ClaimDetailsDialog
          claim={selectedClaim}
          onClose={() => setSelectedClaim(null)}
          onRevise={() => {
            setRevisingClaim(selectedClaim);
            setSelectedClaim(null);
          }}
        />
      )}
      {revisingClaim && (
        <ReviseClaimDialog claim={revisingClaim} year={year} onClose={() => setRevisingClaim(null)} />
      )}
    </Stack>
  );
}

interface CreateClaimDialogProps {
  year: number;
  onClose: () => void;
}

/**
 * Multi-step dialog to create and submit a new Pillar 2 expense claim:
 * choose an expense type, fill in the details, optionally attach proof, then
 * create the claim and submit it in one flow.
 *
 * @param year - The calendar year the new claim belongs to.
 * @param onClose - Called to dismiss the dialog, whether saved or cancelled.
 * @returns The claim creation dialog.
 */
function CreateClaimDialog({ year, onClose }: CreateClaimDialogProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data: expenseTypes, isLoading: expenseTypesLoading, isError: expenseTypesError } = useExpenseTypes();
  const create = useCreateMyClaim();
  const upload = useUploadMyAttachment();
  const submit = useSubmitMyClaim();
  const proofInputRef = useRef<HTMLInputElement>(null);

  const [expenseTypeId, setExpenseTypeId] = useState<number | ''>('');
  const [expenseGroup, setExpenseGroup] = useState<ExpenseGroupKey>('transport');
  const [amount, setAmount] = useState('');
  const [expenseDate, setExpenseDate] = useState('');
  const [expenseEndDate, setExpenseEndDate] = useState('');
  const [coversPeriod, setCoversPeriod] = useState(false);
  const [description, setDescription] = useState('');
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [draftClaimId, setDraftClaimId] = useState<number | null>(null);
  const [proofUploaded, setProofUploaded] = useState(false);
  const [homeAddress, setHomeAddress] = useState<PostalAddress>(EMPTY_ADDRESS);
  const [workAddress, setWorkAddress] = useState<PostalAddress>(EMPTY_ADDRESS);
  useCommuteAddressPrefill(setHomeAddress, setWorkAddress);

  const selectedType = (expenseTypes ?? []).find((type) => type.id === expenseTypeId);
  // Woon-werk claims carry addresses instead of an amount; Finance calculates the amount on approval.
  // The address fields show as soon as the Woon-werk group is chosen, before a specific allowance is picked.
  const isDistanceBased = selectedType ? selectedType.isDistanceBased : expenseGroup === 'allowances';
  const selectableExpenseTypes = useMemo(
    () => (expenseTypes ?? [])
      .filter((type) => getExpensePresentation(type).key !== 'management' && getExpensePresentation(type).key !== 'housing')
      .sort((left, right) => getExpenseSortOrder(left) - getExpenseSortOrder(right)),
    [expenseTypes],
  );
  const visibleExpenseTypes = selectableExpenseTypes.filter((type) => getExpenseGroup(type) === expenseGroup);
  const parsedAmount = Number(amount.replace(',', '.'));
  const isPending = create.isPending || upload.isPending || submit.isPending;

  const canSave =
    expenseTypeId !== '' &&
    (isDistanceBased ? isCompleteAddress(homeAddress) && isCompleteAddress(workAddress) : parsedAmount > 0) &&
    expenseDate !== '' &&
    (!coversPeriod || (expenseEndDate !== '' && expenseEndDate >= expenseDate)) &&
    (!selectedType?.requiresAttachment || proofFile !== null || proofUploaded);

  const handleFile = (file?: File) => {
    setFileError(null);
    if (!file) return;

    const allowedTypes = new Set(['application/pdf', 'image/jpeg', 'image/png']);
    if (!allowedTypes.has(file.type)) {
      setProofFile(null);
      setFileError(t('mobility.self.proofInvalidType'));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setProofFile(null);
      setFileError(t('mobility.self.proofTooLarge'));
      return;
    }
    setProofFile(file);
  };

  const handleSave = async () => {
    const payload: CreateMyExpenseClaim = {
      year,
      expenseTypeId: Number(expenseTypeId),
      amount: isDistanceBased ? 0 : parsedAmount,
      expenseDate,
      expenseEndDate: coversPeriod ? expenseEndDate : null,
      description: description.trim(),
      homeAddress: isDistanceBased ? trimAddress(homeAddress) : null,
      workAddress: isDistanceBased ? trimAddress(workAddress) : null,
    };

    try {
      // Create/upload/submit are three separate API calls; if a later step fails,
      // re-running this function must not repeat steps that already succeeded
      // (e.g. creating a duplicate draft claim). draftClaimId/proofUploaded record
      // which steps are already done so "retry" only resumes from the failed one.
      const claimId = draftClaimId ?? (await create.mutateAsync(payload)).id;
      setDraftClaimId(claimId);

      if (proofFile && !proofUploaded) {
        await upload.mutateAsync({ claimId, file: proofFile });
        setProofUploaded(true);
      }

      await submit.mutateAsync(claimId);
      enqueueSnackbar(t('mobility.self.createdAndSubmitted'), { variant: 'success' });
      onClose();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error), { variant: 'error' });
    }
  };

  return (
    <Dialog
      open
      onClose={isPending ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      {isPending && <LinearProgress />}
      <DialogTitle sx={{ px: { xs: 2.5, sm: 4 }, pt: 3.5, pb: 2 }}>
        <Typography variant="h5" component="div" fontWeight={750}>
          {t('mobility.self.createTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('mobility.self.createSubtitle')}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2.5, sm: 4 }, pb: 1 }}>
        <Stack spacing={3}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            <Typography variant="body2">{t('mobility.self.pillarTwoIntro')}</Typography>
          </Alert>

          <Box sx={{ order: 2 }}>
            <Typography variant="overline" color="primary.main" fontWeight={800}>
              {t('mobility.self.stepExpenseType')}
            </Typography>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              {t('mobility.self.chooseExpenseType')}
            </Typography>

            {expenseTypesLoading && <LinearProgress sx={{ borderRadius: 1 }} />}
            {expenseTypesError && <Alert severity="error">{t('mobility.self.expenseTypesError')}</Alert>}
            {!expenseTypesLoading && !expenseTypesError && selectableExpenseTypes.length === 0 && (
              <Alert severity="warning">{t('mobility.self.noExpenseTypes')}</Alert>
            )}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 1,
                mb: 2,
              }}
            >
              {(['transport', 'allowances'] as ExpenseGroupKey[]).map((group) => (
                <ExpenseGroupOption
                  key={group}
                  group={group}
                  selected={expenseGroup === group}
                  count={selectableExpenseTypes.filter((type) => getExpenseGroup(type) === group).length}
                  disabled={draftClaimId !== null}
                  onSelect={() => {
                    if (group === expenseGroup) return;
                    setExpenseGroup(group);
                    setExpenseTypeId('');
                  }}
                />
              ))}
            </Box>

            <Typography variant="subtitle2" fontWeight={750} sx={{ mb: 1 }}>
              {t('mobility.self.chooseSpecificExpense')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 1.25,
              }}
            >
              {visibleExpenseTypes.map((type) => (
                <ExpenseTypeOption
                  key={type.id}
                  type={type}
                  selected={expenseTypeId === type.id}
                  disabled={draftClaimId !== null}
                  onSelect={() => setExpenseTypeId(type.id)}
                />
              ))}
            </Box>
          </Box>

          <Accordion disableGutters elevation={0} sx={{ order: 4, border: 1, borderColor: 'divider', borderRadius: '12px !important' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <ReceiptLongOutlinedIcon color="primary" />
                <Box>
                  <Typography fontWeight={700}>{t('mobility.self.eligibilityTitle')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('mobility.self.eligibilitySubtitle')}
                  </Typography>
                </Box>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <EligibilityInformation />
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ order: 2 }} />

          <Box sx={{ order: 1 }}>
            <Typography variant="overline" color="primary.main" fontWeight={800}>
              {t('mobility.self.stepExpenseDetails')}
            </Typography>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              {t('mobility.self.expenseDetailsTitle')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
              }}
            >
              {isDistanceBased ? (
                <CommuteAddressFields
                  homeAddress={homeAddress}
                  workAddress={workAddress}
                  onHomeAddressChange={setHomeAddress}
                  onWorkAddressChange={setWorkAddress}
                  disabled={draftClaimId !== null}
                />
              ) : (
                <TextField
                  label={t('mobility.self.amount')}
                  value={amount}
                  onChange={(event) => setAmount(event.target.value.replace(/[^0-9,.]/g, ''))}
                  inputProps={{ inputMode: 'decimal' }}
                  InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>EUR</Typography> }}
                  disabled={draftClaimId !== null}
                  fullWidth
                />
              )}
              <Stack spacing={0.5}>
                <BelgianDateField
                  label={t('mobility.self.date')}
                  value={expenseDate}
                  onChange={setExpenseDate}
                  disabled={draftClaimId !== null}
                  fullWidth
                />
                <Button
                  size="small"
                  startIcon={<TodayOutlinedIcon />}
                  disabled={draftClaimId !== null}
                  onClick={() => setExpenseDate(todayInBrussels())}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {t('mobility.self.useToday')}
                </Button>
              </Stack>
              <FormControlLabel
                control={(
                  <Switch
                    checked={coversPeriod}
                    onChange={(_event, checked) => {
                      setCoversPeriod(checked);
                      if (!checked) setExpenseEndDate('');
                    }}
                    disabled={draftClaimId !== null}
                  />
                )}
                label={t('mobility.self.expenseCoversPeriod')}
                sx={{ gridColumn: { sm: '1 / -1' } }}
              />
              {coversPeriod && (
                <BelgianDateField
                  label={t('mobility.self.periodEndDate')}
                  value={expenseEndDate}
                  onChange={setExpenseEndDate}
                  disabled={draftClaimId !== null}
                  fullWidth
                />
              )}
              <TextField
                label={t('mobility.self.descriptionOptional')}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                multiline
                minRows={3}
                placeholder={t('mobility.self.descriptionPlaceholder')}
                disabled={draftClaimId !== null}
                sx={{ gridColumn: { sm: '1 / -1' } }}
                fullWidth
              />
            </Box>
          </Box>

          <Box sx={{ order: 3 }}>
            <Typography variant="overline" color="primary.main" fontWeight={800}>
              {t('mobility.self.stepProof')}
            </Typography>
            <Typography variant="h6">{t('mobility.self.proofTitle')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {selectedType?.requiresAttachment
                ? t('mobility.self.proofRequired')
                : t('mobility.self.proofOptional')}
            </Typography>
            <input
              ref={proofInputRef}
              type="file"
              hidden
              accept="application/pdf,image/jpeg,image/png"
              onChange={(event: ChangeEvent<HTMLInputElement>) => handleFile(event.target.files?.[0])}
            />
            <Paper
              variant="outlined"
              onDragOver={(event: DragEvent<HTMLDivElement>) => event.preventDefault()}
              onDrop={(event: DragEvent<HTMLDivElement>) => {
                event.preventDefault();
                handleFile(event.dataTransfer.files?.[0]);
              }}
              sx={{
                borderStyle: 'dashed',
                borderWidth: 2,
                borderColor: fileError ? 'error.main' : proofFile || proofUploaded ? 'success.main' : 'divider',
                bgcolor: proofFile || proofUploaded ? 'action.selected' : 'action.hover',
                borderRadius: 2.5,
                p: 2.5,
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'background.paper',
                    color: proofFile || proofUploaded ? 'success.main' : 'primary.main',
                  }}
                >
                  {proofFile || proofUploaded ? <AttachFileIcon /> : <UploadFileOutlinedIcon />}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={700} noWrap>
                    {proofFile?.name ?? (proofUploaded ? t('mobility.self.proofUploaded') : t('mobility.self.proofDropTitle'))}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {proofFile ? formatFileSize(proofFile.size) : t('mobility.self.proofFormats')}
                  </Typography>
                </Box>
                {!proofUploaded && (
                  <Button variant="outlined" onClick={() => proofInputRef.current?.click()} disabled={isPending}>
                    {proofFile ? t('mobility.self.replaceProof') : t('mobility.self.chooseProof')}
                  </Button>
                )}
              </Stack>
            </Paper>
            {fileError && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
                {fileError}
              </Typography>
            )}
          </Box>

          {draftClaimId !== null && (
            <Alert severity="warning">{t('mobility.self.draftCreatedRetry')}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2.5, sm: 4 }, py: 2.5, mt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} disabled={isPending}>{t('common.cancel')}</Button>
        <Button variant="contained" startIcon={<SendIcon />} onClick={handleSave} disabled={!canSave || isPending}>
          {draftClaimId ? t('mobility.self.retrySubmit') : t('mobility.self.createAndSubmit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Read-only dialog showing a claim's full details: type, amount, dates,
 * description, status, rejection reason (if rejected), and attachments with
 * download links. Offers a "revise and resubmit" action for rejected claims.
 *
 * @param claim - The claim to display.
 * @param onClose - Called to dismiss the dialog.
 * @param onRevise - Called when the employee chooses to revise a rejected claim.
 * @returns The claim details dialog.
 */
function ClaimDetailsDialog({
  claim,
  onClose,
  onRevise,
}: {
  claim: ExpenseClaim;
  onClose: () => void;
  onRevise: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const lastDecision = [...claim.decisions].sort((a, b) => b.decidedAtUtc.localeCompare(a.decidedAtUtc))[0];

  const download = async (attachment: ExpenseClaim['attachments'][number]) => {
    try {
      await mobilitySelfApi.downloadMyAttachment(claim.id, attachment);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error), { variant: 'error' });
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
          <Box>
            <Typography variant="h5" fontWeight={750}>{t('mobility.self.claimDetailsTitle')}</Typography>
            <Typography variant="body2" color="text.secondary">#{claim.id} · {claim.expenseTypeName}</Typography>
          </Box>
          <Chip label={t(`mobility.claimStatus.${claim.status}`)} color={getClaimStatusColor(claim.status)} />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.25}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
            <Detail label={t('mobility.self.expenseType')} value={claim.expenseTypeName} />
            <Detail label={t('mobility.self.amount')} value={formatClaimAmount(claim, i18n.language, t('mobility.self.amountToBeCalculated'))} />
            <Detail
              label={claim.expenseEndDate ? t('mobility.self.expensePeriod') : t('mobility.self.date')}
              value={claim.expenseEndDate
                ? `${formatBelgianDate(claim.expenseDate)} – ${formatBelgianDate(claim.expenseEndDate)}`
                : formatBelgianDate(claim.expenseDate)}
            />
            <Detail label={t('mobility.fields.status')} value={t(`mobility.claimStatus.${claim.status}`)} />
          </Box>
          {isDistanceClaim(claim) && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
              <Detail label={t('mobility.self.homeAddress')} value={formatAddress(claim.homeAddress)} />
              <Detail label={t('mobility.self.workAddress')} value={formatAddress(claim.workAddress)} />
              {claim.distanceKm != null && claim.pricePerKm != null && (
                <Detail
                  label={t('mobility.self.distanceCalculation')}
                  value={t('mobility.self.distanceBreakdown', {
                    km: new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(claim.distanceKm),
                    rate: formatCurrency(claim.pricePerKm, claim.currency, i18n.language, 4),
                  })}
                />
              )}
            </Box>
          )}
          <Detail label={t('mobility.self.description')} value={claim.description || t('mobility.self.noDescription')} />
          {claim.status === 'Rejected' && (
            <Alert severity="error">
              <Typography fontWeight={700}>{t('mobility.self.rejectionReason')}</Typography>
              <Typography variant="body2">{lastDecision?.comment?.trim() || t('mobility.self.noRejectionReason')}</Typography>
            </Alert>
          )}
          <Box>
            <Typography variant="subtitle2" fontWeight={750} sx={{ mb: 1 }}>{t('mobility.self.attachmentsTitle')}</Typography>
            {claim.attachments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{t('mobility.self.noAttachments')}</Typography>
            ) : claim.attachments.map((attachment) => (
              <Button key={attachment.id} startIcon={<DownloadOutlinedIcon />} onClick={() => void download(attachment)}>
                {attachment.fileName}
              </Button>
            ))}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>{t('common.close')}</Button>
        {claim.status === 'Rejected' && (
          <Button variant="contained" startIcon={<EditOutlinedIcon />} onClick={onRevise}>
            {t('mobility.self.reviseAndResubmit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * Renders a single label/value pair used in the claim details grid.
 *
 * @param label - The field's label.
 * @param value - The field's display value.
 * @returns The label/value block.
 */
function Detail({ label, value }: { label: string; value: string }) {
  return <Box><Typography variant="caption" color="text.secondary">{label}</Typography><Typography fontWeight={650} sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{value}</Typography></Box>;
}

/**
 * Whether a claim is a woon-werk claim filed with home and work addresses,
 * whose amount Finance calculates from the distance on approval.
 *
 * @param claim - The claim to inspect.
 * @returns True when the claim carries commute addresses.
 */
function isDistanceClaim(claim: ExpenseClaim) {
  return Boolean(claim.homeAddress || claim.workAddress);
}

/**
 * Formats a claim's amount for display. A woon-werk claim has no amount until
 * Finance enters the distance, so it shows a "to be calculated" label while
 * pending and a dash once it was closed without a calculation.
 *
 * @param claim - The claim to format.
 * @param locale - The locale to format with.
 * @param pendingLabel - The label for a woon-werk claim still awaiting calculation.
 * @returns The formatted amount or placeholder.
 */
function formatClaimAmount(claim: ExpenseClaim, locale: string, pendingLabel: string) {
  if (isDistanceClaim(claim) && claim.distanceKm == null) {
    return ['Draft', 'Submitted', 'UnderReview'].includes(claim.status) ? pendingLabel : '—';
  }
  return formatCurrency(claim.amount, claim.currency, locale);
}

/**
 * Prefills the woon-werk address fields once from the employee's most recent
 * woon-werk claim, without overwriting anything already typed.
 *
 * @param setHomeAddress - Setter for the home address field.
 * @param setWorkAddress - Setter for the work address field.
 */
function useCommuteAddressPrefill(
  setHomeAddress: (update: (current: PostalAddress) => PostalAddress) => void,
  setWorkAddress: (update: (current: PostalAddress) => PostalAddress) => void,
) {
  const { data: lastAddresses } = useMyCommuteAddresses();
  const prefilled = useRef(false);
  useEffect(() => {
    if (!lastAddresses || prefilled.current) return;
    prefilled.current = true;
    setHomeAddress((current) => (isEmptyAddress(current) && lastAddresses.homeAddress ? lastAddresses.homeAddress : current));
    setWorkAddress((current) => (isEmptyAddress(current) && lastAddresses.workAddress ? lastAddresses.workAddress : current));
  }, [lastAddresses, setHomeAddress, setWorkAddress]);
}

const EMPTY_ADDRESS: PostalAddress = { street: '', houseNumber: '', postalCode: '', city: '' };

/** Whether every part of the address (street, number, postal code, municipality) is filled in. */
function isCompleteAddress(address: PostalAddress) {
  return [address.street, address.houseNumber, address.postalCode, address.city].every((part) => part.trim() !== '');
}

/** Whether no part of the address has been filled in yet. */
function isEmptyAddress(address: PostalAddress) {
  return [address.street, address.houseNumber, address.postalCode, address.city].every((part) => part.trim() === '');
}

/** Returns the address with every part trimmed, for the API payload. */
function trimAddress(address: PostalAddress): PostalAddress {
  return {
    street: address.street.trim(),
    houseNumber: address.houseNumber.trim(),
    postalCode: address.postalCode.trim(),
    city: address.city.trim(),
  };
}

/**
 * Formats an address on one line, e.g. "Kerkstraat 1, 9000 Gent".
 *
 * @param address - The address to format.
 * @returns The formatted address, or a dash when there is none.
 */
function formatAddress(address?: PostalAddress | null) {
  if (!address) return '—';
  return `${address.street} ${address.houseNumber}, ${address.postalCode} ${address.city}`;
}

/**
 * The home and work address inputs shown instead of the amount for a
 * woon-werk claim, with an explanation that Finance calculates the amount.
 *
 * @param homeAddress - The current home address.
 * @param workAddress - The current work address.
 * @param onHomeAddressChange - Called with the edited home address.
 * @param onWorkAddressChange - Called with the edited work address.
 * @param disabled - Whether the inputs are locked.
 * @returns The address inputs.
 */
function CommuteAddressFields({
  homeAddress,
  workAddress,
  onHomeAddressChange,
  onWorkAddressChange,
  disabled,
}: {
  homeAddress: PostalAddress;
  workAddress: PostalAddress;
  onHomeAddressChange: (value: PostalAddress) => void;
  onWorkAddressChange: (value: PostalAddress) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Alert severity="info" sx={{ gridColumn: { sm: '1 / -1' } }}>
        {t('mobility.self.distanceCalculationInfo')}
      </Alert>
      <AddressFields
        title={t('mobility.self.homeAddress')}
        address={homeAddress}
        onChange={onHomeAddressChange}
        disabled={disabled}
      />
      <AddressFields
        title={t('mobility.self.workAddress')}
        address={workAddress}
        onChange={onWorkAddressChange}
        disabled={disabled}
      />
    </>
  );
}

/**
 * Separate street, number, postal code and municipality inputs for one address.
 * Spans the full width of the surrounding details grid.
 *
 * @param title - The heading for the address (home or work).
 * @param address - The current address.
 * @param onChange - Called with the edited address.
 * @param disabled - Whether the inputs are locked.
 * @returns The address inputs.
 */
function AddressFields({
  title,
  address,
  onChange,
  disabled,
}: {
  title: string;
  address: PostalAddress;
  onChange: (value: PostalAddress) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const update = (part: keyof PostalAddress) => (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ ...address, [part]: event.target.value });

  return (
    <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
      <Typography variant="subtitle2" fontWeight={750} sx={{ mb: 1 }}>{title}</Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '2fr 1fr', sm: '3fr 1fr 1fr 2fr' },
          gap: 1.5,
        }}
      >
        <TextField
          label={t('mobility.self.street')}
          value={address.street}
          onChange={update('street')}
          inputProps={{ maxLength: 200 }}
          autoComplete="off"
          required
          disabled={disabled}
          fullWidth
        />
        <TextField
          label={t('mobility.self.houseNumber')}
          value={address.houseNumber}
          onChange={update('houseNumber')}
          inputProps={{ maxLength: 20 }}
          autoComplete="off"
          required
          disabled={disabled}
          fullWidth
        />
        <TextField
          label={t('mobility.self.postalCode')}
          value={address.postalCode}
          onChange={update('postalCode')}
          inputProps={{ maxLength: 10, inputMode: 'numeric' }}
          autoComplete="off"
          required
          disabled={disabled}
          fullWidth
        />
        <TextField
          label={t('mobility.self.city')}
          value={address.city}
          onChange={update('city')}
          inputProps={{ maxLength: 100 }}
          autoComplete="off"
          required
          disabled={disabled}
          fullWidth
        />
      </Box>
    </Box>
  );
}

/**
 * Dialog to revise a rejected claim's details and resubmit it. Once the
 * revision has been saved (or a replacement proof uploaded), the
 * corresponding fields are locked to prevent re-submitting stale edits if
 * the resubmit step is retried after a failure — the same
 * create/upload/submit resumability pattern as {@link CreateClaimDialog}.
 *
 * @param claim - The rejected claim to revise.
 * @param year - The claim's calendar year.
 * @param onClose - Called to dismiss the dialog, whether resubmitted or cancelled.
 * @returns The revision dialog.
 */
function ReviseClaimDialog({ claim, year, onClose }: { claim: ExpenseClaim; year: number; onClose: () => void }) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data: expenseTypes } = useExpenseTypes();
  const revise = useReviseMyRejectedClaim();
  const upload = useUploadMyAttachment();
  const submit = useSubmitMyClaim();
  const [expenseTypeId, setExpenseTypeId] = useState(claim.expenseTypeId);
  const [amount, setAmount] = useState(claim.amount > 0 ? String(claim.amount) : '');
  const [homeAddress, setHomeAddress] = useState<PostalAddress>(claim.homeAddress ?? EMPTY_ADDRESS);
  const [workAddress, setWorkAddress] = useState<PostalAddress>(claim.workAddress ?? EMPTY_ADDRESS);
  useCommuteAddressPrefill(setHomeAddress, setWorkAddress);
  const [expenseDate, setExpenseDate] = useState(claim.expenseDate);
  const [expenseEndDate, setExpenseEndDate] = useState(claim.expenseEndDate ?? '');
  const [coversPeriod, setCoversPeriod] = useState(Boolean(claim.expenseEndDate));
  const [description, setDescription] = useState(claim.description);
  const [file, setFile] = useState<File | null>(null);
  const [revised, setRevised] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);
  const pending = revise.isPending || upload.isPending || submit.isPending;
  const parsedAmount = Number(amount.replace(',', '.'));
  const isDistanceBased = (expenseTypes ?? []).find((type) => type.id === expenseTypeId)?.isDistanceBased ?? false;
  const amountOrAddressesValid = isDistanceBased
    ? isCompleteAddress(homeAddress) && isCompleteAddress(workAddress)
    : parsedAmount > 0;

  const save = async () => {
    try {
      if (!revised) {
        await revise.mutateAsync({
          claimId: claim.id,
          payload: {
            year,
            expenseTypeId,
            amount: isDistanceBased ? 0 : parsedAmount,
            expenseDate,
            expenseEndDate: coversPeriod ? expenseEndDate : null,
            description: description.trim(),
            homeAddress: isDistanceBased ? trimAddress(homeAddress) : null,
            workAddress: isDistanceBased ? trimAddress(workAddress) : null,
          },
        });
        setRevised(true);
      }
      if (file && !fileUploaded) {
        await upload.mutateAsync({ claimId: claim.id, file });
        setFileUploaded(true);
      }
      await submit.mutateAsync(claim.id);
      enqueueSnackbar(t('mobility.self.resubmitted'), { variant: 'success' });
      onClose();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error), { variant: 'error' });
    }
  };

  return (
    <Dialog open onClose={pending ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
      {pending && <LinearProgress />}
      <DialogTitle><Typography variant="h5" fontWeight={750}>{t('mobility.self.reviseTitle')}</Typography></DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Alert severity="info">{t('mobility.self.reviseExplanation')}</Alert>
          <TextField disabled={revised} select label={t('mobility.self.expenseType')} value={expenseTypeId} onChange={(e) => setExpenseTypeId(Number(e.target.value))}>
            {(expenseTypes ?? []).filter((type) => getExpensePresentation(type).key !== 'management' && (getExpensePresentation(type).key !== 'housing' || type.id === claim.expenseTypeId)).map((type) => <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>)}
          </TextField>
          {isDistanceBased ? (
            <CommuteAddressFields
              homeAddress={homeAddress}
              workAddress={workAddress}
              onHomeAddressChange={setHomeAddress}
              onWorkAddressChange={setWorkAddress}
              disabled={revised}
            />
          ) : (
            <TextField disabled={revised} label={t('mobility.self.amount')} value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9,.]/g, ''))} inputProps={{ inputMode: 'decimal' }} />
          )}
          <Stack spacing={0.5}>
            <BelgianDateField disabled={revised} label={t('mobility.self.date')} value={expenseDate} onChange={setExpenseDate} fullWidth />
            <Button disabled={revised} size="small" startIcon={<TodayOutlinedIcon />} onClick={() => setExpenseDate(todayInBrussels())} sx={{ alignSelf: 'flex-start' }}>{t('mobility.self.useToday')}</Button>
          </Stack>
          <FormControlLabel
            control={<Switch disabled={revised} checked={coversPeriod} onChange={(_event, checked) => { setCoversPeriod(checked); if (!checked) setExpenseEndDate(''); }} />}
            label={t('mobility.self.expenseCoversPeriod')}
          />
          {coversPeriod && <BelgianDateField disabled={revised} label={t('mobility.self.periodEndDate')} value={expenseEndDate} onChange={setExpenseEndDate} fullWidth />}
          <TextField disabled={revised} label={t('mobility.self.descriptionOptional')} value={description} onChange={(e) => setDescription(e.target.value)} multiline minRows={3} />
          <Button component="label" variant="outlined" startIcon={<AttachFileIcon />} disabled={fileUploaded}>
            {file?.name ?? t('mobility.self.addReplacementProof')}
            <input hidden type="file" accept="application/pdf,image/jpeg,image/png" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={pending}>{t('common.cancel')}</Button>
        <Button variant="contained" startIcon={<SendIcon />} onClick={() => void save()} disabled={pending || expenseTypeId <= 0 || !amountOrAddressesValid || !expenseDate || (coversPeriod && (!expenseEndDate || expenseEndDate < expenseDate))}>{t('mobility.self.resubmit')}</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Gets today's date, evaluated in the `Europe/Brussels` timezone, as an ISO
 * `yyyy-MM-dd` string (the `en-CA` locale produces ISO-ordered output).
 *
 * @returns Today's date in Brussels, formatted as `yyyy-MM-dd`.
 */
function todayInBrussels() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

type ExpensePresentation = {
  key: string;
  icon: ReactNode;
};

type ExpenseGroupKey = 'transport' | 'housing' | 'allowances';

// Expense types come from the backend catalog by name only, with no explicit
// category/icon metadata, so presentation (icon + i18n key) is derived here by
// matching Dutch keywords in the name. Keep this in sync with the expense type
// catalog's naming conventions if new expense types are added.
/**
 * Classifies an expense type for display, deriving a presentation key
 * (used for i18n and grouping) and an icon from Dutch keywords in its name.
 *
 * @param type - The expense type to classify.
 * @returns The matched {@link ExpensePresentation}, or the `'other'` fallback if no keyword matches.
 */
function getExpensePresentation(type: ExpenseType): ExpensePresentation {
  const name = type.name.toLocaleLowerCase('nl-BE');
  if (type.isHousingRelated || name.includes('huisvest')) return { key: 'housing', icon: <HomeWorkOutlinedIcon /> };
  if (name.includes('openbaar')) return { key: 'publicTransport', icon: <TrainOutlinedIcon /> };
  if (name.includes('fietspremie') || name.includes('bedrijfsfiets') || name.includes('fietsvergoeding')) {
    return { key: 'bicycleAllowance', icon: <DirectionsBikeOutlinedIcon /> };
  }
  if (name.includes('zachte') || name.includes('fiets') && !name.includes('vergoeding')) {
    return { key: 'softMobility', icon: <DirectionsBikeOutlinedIcon /> };
  }
  if (name.includes('gemeenschappelijk')) return { key: 'collectiveTransport', icon: <GroupsOutlinedIcon /> };
  if (name.includes('deel') || name.includes('taxi') || name.includes('huur')) {
    return { key: 'sharedMobility', icon: <CommuteOutlinedIcon /> };
  }
  if (name.includes('mobiliteitsdienst')) return { key: 'mobilityServices', icon: <DirectionsBusOutlinedIcon /> };
  if (name.includes('parkeer')) return { key: 'parking', icon: <LocalParkingOutlinedIcon /> };
  if (name.includes('voetganger')) return { key: 'pedestrian', icon: <DirectionsWalkOutlinedIcon /> };
  if (name.includes('beheer')) return { key: 'management', icon: <ReceiptLongOutlinedIcon /> };
  return { key: 'other', icon: <ReceiptLongOutlinedIcon /> };
}

/**
 * Maps an expense type to the top-level group it's filed under in the
 * "choose expense type" step (transport, housing, or allowances).
 *
 * @param type - The expense type to group.
 * @returns The expense type's {@link ExpenseGroupKey}.
 */
function getExpenseGroup(type: ExpenseType): ExpenseGroupKey {
  const presentation = getExpensePresentation(type);
  if (presentation.key === 'housing') return 'housing';
  if (presentation.key === 'bicycleAllowance' || presentation.key === 'pedestrian') return 'allowances';
  return 'transport';
}

/**
 * Determines the fixed display order for an expense type within its group,
 * based on a hand-curated priority list of presentation keys.
 *
 * @param type - The expense type to rank.
 * @returns A sort key; unrecognized presentation keys sort last.
 */
function getExpenseSortOrder(type: ExpenseType) {
  const order = [
    'softMobility',
    'publicTransport',
    'parking',
    'sharedMobility',
    'collectiveTransport',
    'mobilityServices',
    'housing',
    'bicycleAllowance',
    'pedestrian',
  ];
  const index = order.indexOf(getExpensePresentation(type).key);
  return index < 0 ? order.length : index;
}

/**
 * Selectable tile for one top-level expense group in the claim creation wizard.
 *
 * @param group - The expense group this tile represents.
 * @param selected - Whether this group is currently selected.
 * @param count - The number of expense types available in this group.
 * @param disabled - Whether selection is disabled (e.g. claim already created).
 * @param onSelect - Called when the tile is chosen.
 * @returns The group option tile.
 */
function ExpenseGroupOption({
  group,
  selected,
  count,
  disabled,
  onSelect,
}: {
  group: ExpenseGroupKey;
  selected: boolean;
  count: number;
  disabled: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const icons: Record<ExpenseGroupKey, ReactNode> = {
    transport: <CommuteOutlinedIcon />,
    housing: <HomeWorkOutlinedIcon />,
    allowances: <DirectionsWalkOutlinedIcon />,
  };

  return (
    <ButtonBase
      onClick={onSelect}
      disabled={disabled || count === 0}
      sx={{ borderRadius: 2, textAlign: 'left', alignItems: 'stretch' }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          p: 1.4,
          borderRadius: 2,
          borderWidth: selected ? 2 : 1,
          borderColor: selected ? 'primary.main' : 'divider',
          bgcolor: selected ? 'action.selected' : 'background.paper',
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box sx={{ color: selected ? 'primary.main' : 'text.secondary', display: 'flex' }}>
            {icons[group]}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={750}>
              {t(`mobility.expenseGroups.${group}.title`)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t(`mobility.expenseGroups.${group}.subtitle`)}
            </Typography>
          </Box>
          <Chip size="small" label={count} />
        </Stack>
      </Paper>
    </ButtonBase>
  );
}

/**
 * Selectable tile for a single specific expense type within the chosen group.
 *
 * @param type - The expense type this tile represents.
 * @param selected - Whether this expense type is currently selected.
 * @param disabled - Whether selection is disabled (e.g. claim already created).
 * @param onSelect - Called when the tile is chosen.
 * @returns The expense type option tile.
 */
function ExpenseTypeOption({
  type,
  selected,
  disabled,
  onSelect,
}: {
  type: ExpenseType;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const presentation = getExpensePresentation(type);

  return (
    <ButtonBase
      onClick={onSelect}
      disabled={disabled}
      sx={{ borderRadius: 2, textAlign: 'left', alignItems: 'stretch' }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          p: 1.75,
          borderRadius: 2,
          borderWidth: selected ? 2 : 1,
          borderColor: selected ? 'primary.main' : 'divider',
          bgcolor: selected ? 'action.selected' : 'background.paper',
          transition: 'border-color 120ms ease, background-color 120ms ease',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              flex: '0 0 auto',
              width: 40,
              height: 40,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              bgcolor: selected ? 'primary.main' : 'action.hover',
              color: selected ? 'primary.contrastText' : 'text.secondary',
            }}
          >
            {presentation.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography fontWeight={700}>{type.name}</Typography>
              {type.requiresAttachment && <Chip size="small" label={t('mobility.self.proofChip')} />}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {t(`mobility.claimCategories.${presentation.key}.short`)}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </ButtonBase>
  );
}

/**
 * Static, collapsible reference panel explaining which mobility expenses are
 * eligible for reimbursement under Pillar 2, shown in the claim creation dialog.
 *
 * @returns The eligibility information panel.
 */
function EligibilityInformation() {
  const { t } = useTranslation();
  const sections = [
    'softMobility',
    'publicTransport',
    'collectiveAndShared',
    'allowances',
  ];

  return (
    <Stack spacing={2}>
      {sections.map((section) => (
        <Box key={section}>
          <Typography variant="subtitle2" fontWeight={750}>
            {t(`mobility.eligibility.${section}.title`)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(`mobility.eligibility.${section}.description`)}
          </Typography>
        </Box>
      ))}
      <Alert severity="warning" icon={false} sx={{ py: 0.75 }}>
        <Typography variant="body2">{t('mobility.self.eligibilityWarning')}</Typography>
      </Alert>
    </Stack>
  );
}

/**
 * Formats a byte count as a rounded KB or MB display string.
 *
 * @param bytes - The file size in bytes.
 * @returns The size formatted as `"N KB"` (below 1 MB) or `"N.N MB"`.
 */
function formatFileSize(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
